import React, { useState, useEffect } from 'react';
import { Plus, ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { PermissionGate } from '../../components/ui/PermissionGate';

import { 
  startOfDay, 
  endOfDay, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth, 
  addDays, 
  subMonths, 
  addMonths, 
  format 
} from 'date-fns';
import { appointmentService } from '../../services/appointmentService';
import { serviceConfigService } from '../../services/serviceConfigService';
import { sampleDataService } from '../../services/sampleDataService';

import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import CalendarMonthView from './components/CalendarMonthView';
import CalendarWeekView from './components/CalendarWeekView';
import CalendarDayView from './components/CalendarDayView';
import CalendarFilters from './components/CalendarFilters';

import ComponentIdBadge from '../../components/ui/ComponentIdBadge';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import SampleDataToggle from '../../components/ui/SampleDataToggle';
import { useRealtimeEntity } from '../../hooks/useRealtimeEntity';
import AppointmentModal from './components/AppointmentModal';




export default function CalendarPage() {
  const { profile, userRoles, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [showSampleData, setShowSampleData] = useState(false);
  const [sampleAppointments, setSampleAppointments] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    serviceId: '',
    assignedTo: ''
  });

  const tenantId = profile?.tenant_id;

  // Add authentication check
  useEffect(() => {
    if (!profile) {
      navigate('/login');
    }
  }, [profile, navigate]);

  // Real-time appointments synchronization
  const transformAppointment = (dbAppointment) => ({
    id: dbAppointment?.id,
    tenantId: dbAppointment?.tenant_id,
    title: dbAppointment?.title,
    description: dbAppointment?.description,
    customerId: dbAppointment?.customer_id,
    serviceId: dbAppointment?.service_id,
    assignedTo: dbAppointment?.assigned_to,
    startTime: dbAppointment?.start_time,
    endTime: dbAppointment?.end_time,
    status: dbAppointment?.status,
    recurrence: dbAppointment?.recurrence,
    recurrenceEndDate: dbAppointment?.recurrence_end_date,
    notes: dbAppointment?.notes,
    location: dbAppointment?.location,
    createdAt: dbAppointment?.created_at,
    updatedAt: dbAppointment?.updated_at
  });

  const { data: realtimeAppointments, setData: setRealtimeAppointments, isConnected } = useRealtimeEntity(
    'appointments',
    appointments,
    transformAppointment
  );

  // Sync real-time data with local state
  useEffect(() => {
    if (isConnected) {
      setAppointments(realtimeAppointments);
    }
  }, [realtimeAppointments, isConnected]);

  useEffect(() => {
    if (tenantId) {
      loadData();
    }
  }, [tenantId, currentDate, filters]);

  // Load appointments
  const loadAppointments = async () => {
    try {
      setLoading(true);
      setError('');

      // Super Admins see ALL appointments across ALL tenants
      // Business Admins and below see only their tenant's appointments
      const superAdmin = isSuperAdmin();
      const tenantId = superAdmin ? null : profile?.tenant_id;
      const data = await appointmentService?.getAll(tenantId, superAdmin);

      setAppointments(data);
    } catch (err) {
      setError(err?.message || 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleSampleDataToggle = async (isActive) => {
    try {
      setShowSampleData(isActive);
      if (isActive) {
        const superAdmin = isSuperAdmin();
        const tenantId = superAdmin ? null : profile?.tenant_id;
        const sampleData = await sampleDataService?.getSampleDataByEntity('appointments', tenantId);
        setSampleAppointments(sampleData);
      } else {
        setSampleAppointments([]);
      }
    } catch (err) {
      console.error('Error loading sample data:', err);
      setError('Failed to load sample data');
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const dateRange = getDateRange();
      const appointmentFilters = {
        ...filters,
        startDate: dateRange?.start?.toISOString(),
        endDate: dateRange?.end?.toISOString()
      };

      const [appointmentsData, servicesData] = await Promise.all([
        appointmentService?.getAll(tenantId, appointmentFilters),
        serviceConfigService?.getAllServices(tenantId)
      ]);

      // Combine real and sample data if sample data is active
      const allAppointments = showSampleData ? [...appointmentsData, ...sampleAppointments] : appointmentsData;
      setAppointments(allAppointments);
      setServices(servicesData);
    } catch (err) {
      setError(err?.message);
      console.error('Error loading calendar data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = () => {
    if (view === 'day') {
      return {
        start: startOfDay(currentDate),
        end: endOfDay(currentDate)
      };
    } else if (view === 'week') {
      return {
        start: startOfWeek(currentDate),
        end: endOfWeek(currentDate)
      };
    } else {
      return {
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate)
      };
    }
  };

  const handlePreviousPeriod = () => {
    if (view === 'day') {
      setCurrentDate(addDays(currentDate, -1));
    } else if (view === 'week') {
      setCurrentDate(addDays(currentDate, -7));
    } else {
      setCurrentDate(subMonths(currentDate, 1));
    }
  };

  const handleNextPeriod = () => {
    if (view === 'day') {
      setCurrentDate(addDays(currentDate, 1));
    } else if (view === 'week') {
      setCurrentDate(addDays(currentDate, 7));
    } else {
      setCurrentDate(addMonths(currentDate, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setShowAppointmentModal(true);
  };

  const handleNewAppointment = (dateTime = null) => {
    setSelectedAppointment(dateTime ? { startTime: dateTime } : null);
    setShowAppointmentModal(true);
  };
  
  const handleDateClick = (date) => {
    handleNewAppointment(date);
  };

  const handleAddAppointment = (date) => {
    setSelectedAppointment({ startTime: date });
    setShowAppointmentModal(true);
  };

  const handleSaveAppointment = async (appointmentData) => {
    try {
      const appointmentWithTenant = {
        ...appointmentData,
        tenantId: profile?.tenant_id
      };
      
      if (selectedAppointment?.id) {
        await appointmentService?.update(selectedAppointment?.id, appointmentWithTenant);
      } else {
        await appointmentService?.create(appointmentWithTenant);
      }
      await loadAppointments();
      setShowAppointmentModal(false);
      setSelectedAppointment(null);
    } catch (err) {
      setError(err?.message || 'Failed to save appointment');
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    try {
      await appointmentService?.delete(appointmentId);
      await loadAppointments();
      setShowAppointmentModal(false);
      setSelectedAppointment(null);
    } catch (err) {
      setError(err?.message || 'Failed to delete appointment');
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const getCurrentPeriodLabel = () => {
    if (view === 'day') {
      return format(currentDate, 'MMMM d, yyyy');
    } else if (view === 'week') {
      const weekStart = startOfWeek(currentDate);
      const weekEnd = endOfWeek(currentDate);
      return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
    } else {
      return format(currentDate, 'MMMM yyyy');
    }
  };

  if (loading && appointments?.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Calendar</h1>
          <p className="text-gray-500 mt-1">Manage appointments and schedules</p>
        </div>
        <div className="flex items-center gap-3">
          <SampleDataToggle
            entityType="appointments"
            onToggle={handleSampleDataToggle}
            isActive={showSampleData}
          />
          <PermissionGate feature="calendar" level="write">
            <Button
              onClick={() => handleAddAppointment(new Date())}
              className="flex items-center gap-2"
            >
              <Icon name="Plus" size={16} className="mr-2" />
              New Appointment
            </Button>
          </PermissionGate>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Calendar Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handlePreviousPeriod}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h2 className="text-xl font-semibold text-gray-900">
              {getCurrentPeriodLabel()}
            </h2>
            <button
              onClick={handleNextPeriod}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={handleToday}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
            >
              <CalendarIcon className="w-4 h-4" />
              Today
            </button>
          </div>
          
          {/* View Selector */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            {['month', 'week', 'day']?.map((viewType) => (
              <button
                key={viewType}
                onClick={() => setView(viewType)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  view === viewType
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {viewType?.charAt(0)?.toUpperCase() + viewType?.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <ComponentIdBadge 
          id="cmp-calendar-filters" 
          name="Calendar Filters Component"
          description="View switcher and filter controls"
        />
        <CalendarFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          services={services}
        />
      </div>

      <div className="relative">
        <ComponentIdBadge 
          id="cmp-calendar-view" 
          name="Calendar View Component"
          description="Dynamic calendar grid showing appointments (Month/Week/Day views)"
        />
        {view === 'month' && (
          <CalendarMonthView
            currentDate={currentDate}
            appointments={appointments}
            onAppointmentClick={handleAppointmentClick}
            onDateClick={handleDateClick}
          />
        )}
        {view === 'week' && (
          <CalendarWeekView
            currentDate={currentDate}
            appointments={appointments}
            onAppointmentClick={handleAppointmentClick}
            onDateClick={handleDateClick}
          />
        )}
        {view === 'day' && (
          <CalendarDayView
            currentDate={currentDate}
            appointments={appointments}
            onAppointmentClick={handleAppointmentClick}
            onDateClick={handleDateClick}
          />
        )}
      </div>

      {showAppointmentModal && (
        <AppointmentModal
          appointment={selectedAppointment}
          services={services}
          onSave={handleSaveAppointment}
          onDelete={handleDeleteAppointment}
          onClose={() => {
            setShowAppointmentModal(false);
            setSelectedAppointment(null);
          }}
        />
      )}
    </div>
  );
}